import EditableTable from '../EditableTable';
import {
  clearBenchmarkPreview,
  clearIndexFundPreview,
  deleteIndexFund,
  IndexFund,
  requestBenchmarkPreview,
  requestIndexFundPreview,
  selectors,
  updateIndexFund,
} from 'state';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from 'services';
import CreateIndexFundModal from './CreateIndexFundModal';
import { Button, Collapse, Spin, Typography } from 'antd';
import IndexFundPreviewModal from './IndexFundPreviewModal';
import { findRenderedDOMComponentWithClass } from 'react-dom/test-utils';
import RefreshIndexFundModal from './RefreshIndexFundModal';

const IndexFundsTab: React.FC = () => {
  const dispatch = useDispatch();
  const indexFunds = useSelector(selectors.indexFundsList);
  const waiting = useSelector(selectors.waiting);
  const [CreateModal, showCreateModal] = useModal(CreateIndexFundModal);
  const [refreshModal, showRefreshModal] = useModal(RefreshIndexFundModal);
  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const indexFundData = useSelector(selectors.indexFundPreviewData);

  // Decommissioned funds live in their own collapsible table at the bottom (retired from selection
  // everywhere, but never deleted). Everything else stays in the main table.
  const activeFunds = useMemo(
    () => indexFunds.filter(fund => !fund.decommissioned),
    [indexFunds]
  );
  const decommissionedFunds = useMemo(
    () => indexFunds.filter(fund => fund.decommissioned),
    [indexFunds]
  );

  useEffect(() => {
    if (indexFundData) {
      setPreviewLoading(false);
      setShowPreview(true);
    } else {
      setShowPreview(false);
    }
  }, [indexFundData]);
  useEffect(() => {
    // Cleanup function
    return () => {
      dispatch(clearIndexFundPreview({}));
    };
  }, []);

  const showEodButton = {
    onClick: (record: IndexFund) => {
      setPreviewLoading(true);
      dispatch(requestIndexFundPreview({ index_fund_id: record.id }));
    },
    title: 'Show EoD',
  };
  // Full-record update with only the flag flipped -> update_index_fund round-trips through the
  // serializer, so all required fields are already present on `record`.
  const decommissionButton = {
    onClick: (record: IndexFund) => {
      // fetch_eod is decided server-side on decommission: kept on only if a live real-time session
      // still holds the fund, otherwise turned off. The response carries the resolved value.
      dispatch(updateIndexFund({ ...record, decommissioned: true }));
    },
    title: 'Decommission',
  };
  const restoreButton = {
    onClick: (record: IndexFund) => {
      dispatch(updateIndexFund({ ...record, decommissioned: false }));
    },
    title: 'Restore',
  };

  return (
    <>
      {refreshModal}
      {CreateModal}
      <div style={{ marginBottom: 10 }}>
        <Button type="primary" onClick={() => showCreateModal({})}>
          Create
        </Button>
        <Button
          // type="primary"
          onClick={() => showRefreshModal({})}
          style={{ marginLeft: 10 }}
        >
          Refresh EoD
        </Button>
        {previewLoading && <Spin style={{ marginLeft: 10 }} />}
      </div>
      <Typography.Text type={'secondary'}>
        *Decommissioned funds are retired from every trainer/player benchmark
        surface (kept only where a session already holds them).
      </Typography.Text>
      <EditableTable
        data={activeFunds}
        onSave={(a: IndexFund) => {
          dispatch(updateIndexFund(a));
        }}
        onDelete={(item: IndexFund) => {
          dispatch(deleteIndexFund(item));
        }}
        ignoredFields={['decommissioned', 'hidden']}
        nonEditableFields={['in_playing_sessions', 'in_paused_sessions']}
        buttons={[showEodButton, decommissionButton]}
      />

      <Collapse style={{ marginTop: 16 }}>
        <Collapse.Panel
          header={`Decommissioned funds (${decommissionedFunds.length})`}
          key="decommissioned"
        >
          {decommissionedFunds.length === 0 ? (
            <Typography.Text type={'secondary'}>
              No decommissioned funds.
            </Typography.Text>
          ) : (
            <EditableTable
              data={decommissionedFunds}
              onSave={(a: IndexFund) => {
                dispatch(updateIndexFund(a));
              }}
              onDelete={(item: IndexFund) => {
                dispatch(deleteIndexFund(item));
              }}
              ignoredFields={['decommissioned', 'hidden']}
              nonEditableFields={['in_playing_sessions', 'in_paused_sessions']}
              buttons={[showEodButton, restoreButton]}
            />
          )}
        </Collapse.Panel>
      </Collapse>

      {showPreview && (
        <IndexFundPreviewModal
          visible={showPreview}
          hide={() => {
            setShowPreview(false);
          }}
        />
      )}
    </>
  );
};

export default IndexFundsTab;
